import { LinearProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { API } from '~/api';
import { AppRoutes } from '~/app/app.routes';
import { AuthService } from '~/service/service.auth';
import { OlfactiveService } from '~/service/service.olfactive';
import { UIService } from '~/service/service.ui';
import { useService } from '~/shared/service/service.base';
import { ImportOlfactiveDialog } from '~/view/dialog/dialog.import';
import { ActionsColumn, AvatarColumn, useGridColumns } from '~/view/grid/grid.columns';
import { GridContainer } from '~/view/grid/grid.container';
import { GridNoRows } from '~/view/grid/grid.norows';
import { MuiGridPagination } from '~/view/grid/grid.pagination';
import { GridCsvImportMenuItem, GridToolbarExportMenu, GridToolbarView } from '~/view/grid/grid.toolbar';

export const OlfactiveListPage = observer(() => {
	const [olfactives, ui, auth] = useService(OlfactiveService, UIService, AuthService);
	const columns = useGridColumns<API.OlfactiveListItem>(() => [
		AvatarColumn({ field: 'image', headerName: 'Image' }),
		{ field: 'id', headerName: 'ID', minWidth: 150 },
		{ field: 'name', headerName: 'Name', flex: 1 },
		{ field: 'translations', headerName: 'Translations', sortable: false, flex: 1 },
		ActionsColumn({
			editRoute: AppRoutes.OlfactiveEdit.buildPath,
			onDelete: olfactives.delete,
			permission: 'notes',
		}),
	]);

	ui.usePageTitle('Olfactive Families');
	useEffect(() => olfactives.loadAndSubscribe(), [olfactives]);

	return (
		<GridContainer async={olfactives.async}>
			<DataGrid
				initialState={olfactives.gridState.initial()}
				loading={olfactives.async.loading}
				rowCount={olfactives.totalCount}
				rows={olfactives.items.slice()}
				columns={columns}
				disableColumnFilter
				paginationMode="server"
				sortingMode="server"
				onStateChange={olfactives.gridState.onStateChange}
				slots={{
					loadingOverlay: LinearProgress,
					noRowsOverlay: () => <GridNoRows>No olfactive families found</GridNoRows>,
					pagination: MuiGridPagination,
					toolbar: () => (
						<GridToolbarView
							onReload={olfactives.reload}
							gridState={olfactives.gridState}
							onCreateUrl={auth.can('notes', 'CREATE') ? AppRoutes.OlfactiveNew.buildPath({}) : undefined}
							rightMenu={
								auth.can('notes', 'EXPORT') && (
									<GridToolbarExportMenu>
										<GridCsvImportMenuItem onClick={() => ui.showDialog(ImportOlfactiveDialog)} />
									</GridToolbarExportMenu>
								)
							}
						/>
					),
				}}
			/>
			<ImportOlfactiveDialog />
		</GridContainer>
	);
});
